(() => {
  const ukFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  // England and Wales bank holidays only (source: gov.uk/bank-holidays, england-and-wales). Dispatch is from England.
  const holidays = new Set(["2026-01-01", "2026-04-03", "2026-04-06", "2026-05-04", "2026-05-25", "2026-08-31", "2026-12-25", "2026-12-28", "2027-01-01", "2027-03-26", "2027-03-29", "2027-05-03", "2027-05-31", "2027-08-30", "2027-12-27", "2027-12-28", "2028-01-03", "2028-04-14", "2028-04-17", "2028-05-01", "2028-05-29", "2028-08-28", "2028-12-25", "2028-12-26"]);
  const clock = date => {
    const p = Object.fromEntries(ukFormatter.formatToParts(date).map(part => [part.type, part.value]));
    return { date: `${p.year}-${p.month}-${p.day}`, weekday: p.weekday, seconds: Number(p.hour) * 3600 + Number(p.minute) * 60 + Number(p.second) };
  };
  const dispatchWhen = (now, deadline) => {
    const today = clock(now).date;
    const target = clock(new Date(deadline)).date;
    const days = Math.round((Date.parse(target+'T00:00:00Z')-Date.parse(today+'T00:00:00Z'))/86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    return 'on '+new Intl.DateTimeFormat('en-GB',{timeZone:'Europe/London',weekday:'long'}).format(new Date(deadline));
  };
  const cutoffLabel = hour => hour === 12 ? '12pm' : hour > 12 ? `${hour - 12}pm` : `${hour}am`;
  const absoluteTime = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? Date.parse(value) : NaN;
  const shippingMessage = (config, cart) => {
    if (config.country !== 'GB' || cart.currency && cart.currency !== 'GBP') return 'Delivery options for your destination';
    if (config.eligibilityPending) return 'Updating delivery eligibility…';
    const total = Math.max(0, Number(cart.qualifyingSubtotal) || 0);
    const money = cents => new Intl.NumberFormat('en-GB', {style:'currency',currency:'GBP'}).format(cents / 100);
    if (config.eligibilityUnknown || total === 0) return 'Free UK standard delivery from £30';
    if (total < 3000) return `Add ${money(3000 - total)} for free UK standard delivery`;
    if(Number.isFinite(Number(cart.actualTotal))&&Number(cart.actualTotal)<3000)return config.unconfirmedText||'£30 threshold met before discounts. Delivery confirmed at checkout.';
    if (cart.hasGel) return 'Free UK delivery unlocked · Tracked 24 included';
    if (total < 4000) return `Free UK standard delivery unlocked · Add ${money(4000 - total)} for free express`;
    return 'Free UK standard delivery unlocked · Free express where available';
  };
  const topupParts = (variants, gap) => {
    if (!Number.isSafeInteger(gap) || gap < 1 || gap >= 3000) return [];
    const amounts = [gap - gap % 100, gap % 100].filter(Boolean);
    const parts = amounts.map(amount => variants?.find(v => v.available && v.amount === amount));
    return parts.every(Boolean) ? parts : [];
  };
  const recommendationsFor = (products, cart) => {
    if (!cart.shippingCount || cart.currency !== 'GBP') return [];
    const base = Math.max(0,(cart.qualifyingSubtotal || 0) - (cart.topupTotal || 0));
    const gap = (base < 3000 ? 3000 : 4000) - base;
    if (gap <= 0 || cart.hasGel && base >= 3000) return [];
    return products.filter(product => !cart.productIds?.includes(product.id)).map(product => {
      const variants = product.variants.filter(v => v.available && v.price > 0).sort((a,b) => a.price - b.price);
      const variant = variants.find(v => v.price >= gap) || variants[0];
      return variant ? {...product,variant,covers:variant.price >= gap} : null;
    }).filter(Boolean).sort((a,b) => Number(b.covers) - Number(a.covers) || a.variant.price - b.variant.price).slice(0,3);
  };
  const topupOffer = (config, cart) => {
    if (config.country !== 'GB' || cart.currency !== 'GBP' || config.eligibilityUnknown || config.eligibilityPending) return {mode:'unavailable'};
    if (cart.topupQuantity > 0) return {mode:'included'};
    if (!cart.shippingCount) return {mode:'empty'};
    const total = Number(cart.qualifyingSubtotal) || 0;
    if (cart.hasGel && total >= 3000) return {mode:'unlocked'};
    const threshold = total < 3000 ? 3000 : 4000;
    const gap = threshold - total;
    if (gap <= 0) return {mode:'unlocked'};
    const parts = topupParts(config.topupVariants,gap);
    if (!cart.topupCompatible || cart.totalDiscount !== 0 || cart.actualTotal !== total || !parts.length) return {mode:'shopping'};
    return {mode:'offer',gap,threshold,parts,delivery:threshold === 3000 ? 'standard' : 'express'};
  };
  const nextCutoff = (now, days, hour, excludedDates, through) => {
    const local = clock(now);
    const [year, month, day] = local.date.split('-').map(Number);
    const excluded = new Set(String(excludedDates || '').split(/[\s,;]+/).filter(Boolean));
    for (let offset = 0; offset < 15; offset++) {
      const date = new Date(Date.UTC(year, month - 1, day + offset));
      const key = date.toISOString().slice(0, 10);
      if (key > through || !days.includes(date.getUTCDay()) || holidays.has(key) || excluded.has(key)) continue;
      const wall = date.getTime() + hour * 3600000;
      let epoch = wall;
      for (let pass = 0; pass < 3; pass++) {
        const observed = clock(new Date(epoch));
        epoch += wall - (Date.parse(observed.date + 'T00:00:00Z') + observed.seconds * 1000);
      }
      if (epoch > now.getTime()) return new Date(epoch);
    }
    return null;
  };
  const resolve = (config, cart, now) => {
    const hour = Number(config.cutoffHour) || 12;
    const cutoff = cutoffLabel(hour);
    if (config.paused) return { message: config.serviceMessage?.trim() || 'Please check our current dispatch update', timer: null, summary: 'Same-day dispatch is temporarily paused.' };
    const promoEnd = absoluteTime(config.promoEnd);
    const promoStart = config.promoStart?.trim() ? absoluteTime(config.promoStart) : 0;
    if (config.mode === 'promotion' && config.promoMessage?.trim() && Number.isFinite(promoEnd) && Number.isFinite(promoStart) && promoEnd > promoStart && now.getTime() >= promoStart && now.getTime() < promoEnd) {
      return { message: config.promoMessage.trim(), timer: Math.ceil((promoEnd - now.getTime()) / 1000), promotion: true, summary: 'An offer is currently running. Dispatch schedules below still apply.' };
    }
    if (config.eligibilityPending) return { message: '', timer: null, summary: 'Checking the items in your basket against our dispatch schedule.' };
    if (config.eligibilityUnknown) return { message: '', timer: null, summary: 'Please check the dispatch schedule below for your order.' };
    const isProduct = config.isProduct !== false;
    const kind = isProduct ? (config.kind === 'other' && cart.hasGel ? 'gel' : config.kind) : cart.hasGel || !cart.shippingCount ? 'gel' : 'other';
    if (kind === 'digital') return { message: '', timer: null, summary: 'Digital gift cards do not require postal delivery.' };
    if (kind === 'unavailable') return { message: '', timer: null, summary: 'This product is currently unavailable. Check availability before relying on a dispatch date.' };
    const local = clock(now);
    const deadline = nextCutoff(now, kind === 'gel' ? (config.gelDays || [1, 2, 3, 4]) : (config.otherDays || [1, 2, 3, 4]), hour, config.excludedDates, config.holidayThrough);
    if (deadline) {
      const when = dispatchWhen(now,deadline);
      const orderMessage = isProduct ? `Order ${config.productTitle || 'this product'} within` : 'Order within';
      return { message: orderMessage, suffix: `for dispatch ${when}`, dispatchWhen:when, timer: Math.ceil((deadline - now) / 1000), deadline: deadline.toISOString(), summary: `Order before ${cutoff} UK time for dispatch ${when}. All products dispatch Monday–Thursday. Stock, preparation readiness, bank holidays and closures apply.` };
    }
    return { message: '', timer: null, summary: 'All products dispatch Monday–Thursday. Order before noon UK time on an eligible day. Stock, preparation readiness and closures apply.' };
  };
  const deliveryAim = (config, cart, state) => {
    if (!state.deadline || state.promotion || config.paused || config.eligibilityPending || config.eligibilityUnknown || config.country !== 'GB') return null;
    const gel = config.kind === 'gel' || cart.hasGel;
    const date = clock(new Date(state.deadline)).date;
    const excluded = new Set(String(config.excludedDates || '').split(/[\s,;]+/).filter(Boolean));
    const after = count => {
      const day = new Date(date+'T12:00:00Z');
      for (let guard=0;count>0&&guard<60;guard++) {
        day.setUTCDate(day.getUTCDate()+1);
        const key=day.toISOString().slice(0,10);
        if ([1,2,3,4,5].includes(day.getUTCDay())&&!holidays.has(key)&&!excluded.has(key)) count--;
      }
      return count===0?day.toISOString().slice(0,10):null;
    };
    const start=after(gel?1:2);const end=after(gel?1:3);
    return start&&end&&end<=config.holidayThrough?{start,end}:null;
  };
  const compactMessage = (config, cart, state, now) => {
    const text = config.announcementText || {};
    const value = (key, fallback) => text[key] || fallback;
    if (config.paused) return value('paused','Dispatch update');
    if (state.promotion) return value('promotion','Current offer');
    if (config.country !== 'GB' || cart.currency && cart.currency !== 'GBP') return value('destination','Delivery options');
    if (config.isProduct && config.kind === 'digital') return value('digital','Digital gift cards');
    if (config.isProduct && config.kind === 'unavailable') return value('unavailable','Check product availability');
    if (config.eligibilityPending) return value('pending','Checking dispatch…');
    if (config.eligibilityUnknown) return value('generic','Dispatch & delivery');
    if (state.deadline) {
      const deadline = new Date(state.deadline);
      if (clock(now).date === clock(deadline).date) return value('today','Order by {cutoff} for dispatch today').replace('{cutoff}',cutoffLabel(Number(config.cutoffHour) || 12));
      const date = dispatchWhen(now,deadline);
      return value('next','Order now for dispatch {date}').replace('on {date}','{date}').replace('{date}',date);
    }
    return value('default','Free UK delivery from £30');
  };
  const duration = seconds => {
    const days = Math.floor(seconds / 86400);
    const hours = String(Math.floor(seconds % 86400 / 3600)).padStart(2, '0');
    const minutes = String(Math.floor(seconds % 3600 / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${days ? days + 'd ' : ''}${hours}:${minutes}:${secs}`;
  };
  const announcement = (config,cart,state,now) => {
    const total=Math.max(0,Number(cart.qualifyingSubtotal)||0);
    const uk=config.country==='GB'&&(!cart.currency||cart.currency==='GBP');
    const money=cents=>new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP',maximumFractionDigits:2}).format(cents/100);
    const seconds=Math.max(0,Math.ceil(state.timer||0));
    const hours=String(Math.floor(seconds/3600)).padStart(2,'0');
    const minutes=String(Math.floor(seconds%3600/60)).padStart(2,'0');
    const secs=String(seconds%60).padStart(2,'0');
    const time=`${hours}hrs ${minutes}mins ${secs}secs`;
    let full=compactMessage(config,cart,state,now),mobile=full;
    if(state.promotion&&state.timer!==null){full=`${config.promoMessage.trim()} · Ends in ${time}`;mobile=`Offer ends in ${time}`;}
    if(!uk)return{full,mobile:config.announcementText?.destination||'Delivery options',shipping:'Delivery options at checkout',shippingMobile:'Delivery options'};
    if(config.eligibilityPending)return{full,mobile,shipping:'Updating your delivery total…',shippingMobile:'Updating delivery…'};
    if(config.eligibilityUnknown||!total)return{full,mobile,shipping:config.announcementText?.default || 'FREE UK delivery on orders £30+',shippingMobile:config.announcementText?.default || 'FREE UK delivery on orders £30+'};
    if(total>=3000&&Number.isFinite(Number(cart.actualTotal))&&Number(cart.actualTotal)<3000)return{full,mobile,shipping:config.unconfirmedText||'£30 threshold met before discounts. Delivery confirmed at checkout.',shippingMobile:'£30 before-discount threshold met'};
    if(total>=3000)return{full,mobile,shipping:'FREE UK delivery unlocked',shippingMobile:'FREE UK delivery unlocked'};
    const gap=money(3000-total);
    return{full,mobile,shipping:`You’re ${gap} away from FREE UK delivery`,shippingMobile:`You’re ${gap} away from FREE UK delivery`};
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { dispatchWhen, resolve, compactMessage, clock, duration, absoluteTime, nextCutoff, shippingMessage, topupOffer, topupParts, recommendationsFor, deliveryAim, announcement };
  if (typeof document === 'undefined') return;
  let cleanup = () => {};
  const boot = () => {
    cleanup();
    const bar = document.querySelector('[data-mm-dispatch-root]');
    const configNode = document.querySelector('[data-mm-dispatch-config]');
    const stateNode = document.querySelector('[data-mm-dispatch-state]');
    if (!bar || !configNode || !stateNode) return;
    let config, cart;
    try { config = JSON.parse(configNode.textContent); cart = JSON.parse(stateNode.textContent); } catch { return; }
    if (config.isProduct) config.productTitle = document.querySelector('#MainContent h1')?.textContent.trim() || config.productTitle;
    const dialog = document.getElementById('mm-dispatch-details');
    const trigger = bar.querySelector('[data-mm-dispatch-open]');
    const headline = bar.querySelector('[data-mm-announcement]');
    const summary = dialog.querySelector('[data-mm-dispatch-summary]');
    const message = dialog.querySelector('[data-mm-dispatch-message]');
    const timer = dialog.querySelector('[data-mm-dispatch-timer]');
    const suffix = dialog.querySelector('[data-mm-dispatch-suffix]');
    const shippingHeadline = dialog.querySelector('[data-mm-dispatch-shipping]');
    const topup = dialog.querySelector('[data-mm-topup]');
    const topupAdd = topup.querySelector('[data-mm-topup-add]');
    const topupRemove = topup.querySelector('[data-mm-topup-remove]');
    const topupStatus = topup.querySelector('[data-mm-topup-status]');
    const suggestions = topup.querySelector('[data-mm-topup-suggestions]');
    const suggestionList = topup.querySelector('[data-mm-topup-products]');
    const comparison = topup.querySelector('[data-mm-topup-comparison]');
    let recommendedProducts = [];
    let lastRecommendationSignature = '';
    let topupBusy = false;
    const fill = (text,values) => Object.entries(values).reduce((result,[key,value]) => result.replaceAll(`{${key}}`,value),text);
    const money = cents => new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(cents / 100);
    const renderRecommendations = () => {
      const products = config.eligibilityUnknown || config.eligibilityPending ? [] : recommendationsFor(recommendedProducts,cart);
      suggestions.hidden = !products.length;
      const signature = JSON.stringify(products.map(p => [p.id,p.variant.id,p.variant.price,p.covers]));
      if (signature !== lastRecommendationSignature) {
        suggestionList.replaceChildren(...products.map(product => {
          const article = document.createElement('article'); article.className = 'mm-topup-product';
          const image = document.createElement('img'); image.src = product.image; image.alt = product.title; image.width = 64; image.height = 64; image.loading = 'lazy';
          if (product.image) article.append(image);
          const content = document.createElement('div');
          const link = document.createElement('a'); link.href = `${config.root}products/${encodeURIComponent(product.handle)}?variant=${product.variant.id}`; link.textContent = product.title; content.append(link);
          if (product.variant.title !== 'Default Title') { const variant = document.createElement('p'); variant.textContent = product.variant.title; content.append(variant); }
          const price = document.createElement('strong'); price.textContent = money(product.variant.price); content.append(price);
          const note = document.createElement('p'); note.textContent = product.covers ? config.topupText.product_unlocks : config.topupText.product_progress; content.append(note);
          const add = document.createElement('button'); add.type = 'button'; add.className = 'btn outline'; add.dataset.mmTopupProduct = product.variant.id; add.textContent = config.topupText.product_add; add.setAttribute('aria-label',fill(config.topupText.product_button,{product:product.title,amount:money(product.variant.price)})); content.append(add);
          article.append(content); return article;
        }));
        lastRecommendationSignature = signature;
      }
      suggestionList.querySelectorAll('button').forEach(button => button.disabled = topupBusy || config.eligibilityPending);
    };
    const renderTopup = () => {
      const offer = topupOffer(config,cart);
      const text = config.topupText;
      topup.hidden = config.country !== 'GB' || cart.currency !== 'GBP';
      topupAdd.hidden = offer.mode !== 'offer';
      topupRemove.hidden = offer.mode !== 'included';
      topup.querySelector('[data-mm-topup-note]').hidden = offer.mode !== 'offer' && offer.mode !== 'included';
      topup.querySelector('[data-mm-topup-basket]').hidden = !cart.shippingCount && !cart.topupQuantity;
      topupAdd.disabled = topupRemove.disabled = topupBusy || config.eligibilityPending;
      comparison.hidden = true;
      let copy = config.eligibilityPending ? text.checking : offer.mode === 'empty' ? text.empty : offer.mode === 'included' ? `${text.included} ${shippingMessage(config,cart)}` : offer.mode === 'unlocked' ? (cart.hasGel ? shippingMessage(config,cart) : text.unlocked) : text.unavailable;
      if (offer.mode === 'offer') {
        const amount = offer.gap < 100 ? `${offer.gap}p` : money(offer.gap);
        copy = fill(text.gap,{amount:money(offer.gap),delivery:text[offer.delivery]});
        topupAdd.textContent = fill(text.button,{amount});
        const deliveryCharge = offer.delivery === 'standard' ? 399 : 499;
        if (offer.gap >= deliveryCharge) { comparison.hidden = false; comparison.textContent = fill(offer.gap === deliveryCharge ? text.same_cost : text.lower_cost,{charge:money(deliveryCharge),amount:money(offer.gap)}); }
      }
      const copyNode = topup.querySelector('[data-mm-topup-copy]');
      if (copyNode.textContent !== copy) copyNode.textContent = copy;
      renderRecommendations();
    };
    let stopped = false;
    let requestId = 0;
    let refreshTimeout;
    let lastSignature = '';
    const listeners = [];
    let mobileIndex = 0;
    let hovering = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewport = window.matchMedia('(max-width: 760px)');
    const on = (target, name, handler) => { target.addEventListener(name, handler); listeners.push(() => target.removeEventListener(name, handler)); };
    document.querySelectorAll('[data-mm-dispatch-cutoff], [data-mm-dispatch-inline-cutoff]').forEach(el => { el.textContent = cutoffLabel(Number(config.cutoffHour) || 12); });
    let offerUrl;
    try { const u = new URL(config.promoUrl, location.origin); if (config.promoUrl && (u.origin === location.origin || u.protocol === 'https:')) offerUrl = u.href; } catch {}
    const promoDetails = dialog.querySelector('[data-mm-dispatch-promo-details]');
    if (offerUrl) dialog.querySelector('[data-mm-dispatch-promo-link]').href = offerUrl;
    const render = () => {
      const now = new Date();
      const state = resolve(config, cart, now);
      const barCopy=announcement(config,cart,state,now);
      const shortCopy=barCopy.full+' · '+barCopy.shipping;
      const dialogDispatch = dialog.querySelector('[data-mm-dialog-dispatch]');
      if (dialogDispatch && dialogDispatch.textContent !== barCopy.full) dialogDispatch.textContent = barCopy.full;
      const arrival = dialog.querySelector('[data-mm-dialog-arrival]');
      const aim = deliveryAim(config,cart,state);
      if (arrival) {
        arrival.hidden = !aim;
        const arrivalNote = dialog.querySelector('[data-mm-dialog-arrival-note]');
        if (arrivalNote) arrivalNote.hidden = !aim;
        if (aim) {
          const format = value => new Intl.DateTimeFormat('en-GB',{timeZone:'UTC',weekday:'short',day:'numeric',month:'short'}).format(new Date(value));
          const date = aim.start === aim.end ? format(aim.start) : format(aim.start)+'–'+format(aim.end);
          const copy = (config.overlayArrival || 'Royal Mail aim: {date} · not guaranteed').replace('{date}',date);
          if (arrival.textContent !== copy) arrival.textContent = copy;
        }
      }
      const standardCost = dialog.querySelector('[data-mm-dialog-standard-cost]');
      if (standardCost) standardCost.textContent = Number(cart.qualifyingSubtotal) >= 3000 && Number(cart.actualTotal) < 3000 ? (config.confirmCheckoutText||'Confirmed at checkout') : Number(cart.qualifyingSubtotal) >= 3000 && !config.eligibilityPending && !config.eligibilityUnknown ? config.overlayFree || 'FREE' : '£3.99';
      const basis=dialog.querySelector('[data-mm-dialog-qualifying]');
      if(basis){
        basis.hidden=config.country!=='GB'||cart.currency!=='GBP'||config.eligibilityPending||config.eligibilityUnknown||!cart.shippingCount;
        const amount=new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(Math.max(0,Number(cart.qualifyingSubtotal)||0)/100);
        const copy=(config.qualifyingText||'Qualifying basket value: __AMOUNT__ before discounts.').replace('__AMOUNT__',amount);
        if(basis.textContent!==copy)basis.textContent=copy;
      }
      const progress = dialog.querySelector('[data-mm-dialog-progress]');
      if (progress) {
        progress.hidden = config.country !== 'GB' || cart.currency !== 'GBP' || config.eligibilityPending || config.eligibilityUnknown || !cart.shippingCount;
        progress.value = Math.min(3000,Math.max(0,Number(cart.qualifyingSubtotal)||0));
      }
      if(headline.textContent!==shortCopy||!headline.querySelector('strong')){
        const dispatchCopy=document.createElement('strong');dispatchCopy.textContent=barCopy.full;
        const deliveryCopy=document.createElement('span');deliveryCopy.textContent=' · '+barCopy.shipping;
        headline.replaceChildren(dispatchCopy,deliveryCopy);
      }
      const mobileHeadline=bar.querySelector('[data-mm-announcement-mobile]');
      const mobileCopy=mobileIndex === 0 ? barCopy.mobile : (config.country === 'GB' ? 'Free UK delivery over £30' : barCopy.shippingMobile);
      if(mobileHeadline){
        if(mobileHeadline.textContent!==mobileCopy)mobileHeadline.textContent=mobileCopy;
        mobileHeadline.toggleAttribute('data-mm-dispatch-emphasis',mobileIndex===0);
      }
      trigger.setAttribute('aria-label',shortCopy+'. '+(config.announcementText?.details_label||'Dispatch and delivery details'));
      if(summary.textContent!==state.summary)summary.textContent=state.summary;
      const shippingCopy=shippingMessage(config,cart);
      document.dispatchEvent(new CustomEvent('mm:dispatch:updated',{detail:{state:{...state,paused:Boolean(config.paused),deliveryAim:deliveryAim(config,cart,state)},cart:{...cart,pending:Boolean(config.eligibilityPending),unknown:Boolean(config.eligibilityUnknown)}}}));
      renderTopup();
      if (shippingHeadline && shippingHeadline.textContent !== shippingCopy) shippingHeadline.textContent = shippingCopy;
      const signature = `${state.message}|${state.suffix || ''}|${Boolean(state.promotion)}|${state.timer !== null}`;
      if (signature !== lastSignature) {
        message.textContent = state.message;
        bar.classList.toggle('mm-dispatch-empty', !state.message);
        suffix.textContent = state.suffix || '';
        suffix.hidden = !state.suffix;
        promoDetails.hidden = !state.promotion || !offerUrl;
        lastSignature = signature;
      }
      timer.hidden = state.timer === null;
      if (state.timer !== null) {
        timer.textContent = `${duration(state.timer)}${state.promotion ? ' left' : ''}`;
        timer.setAttribute('aria-label', state.promotion ? `Offer ends at ${new Date(absoluteTime(config.promoEnd)).toLocaleString('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' })} UK time` : `Order cut-off ${new Date(state.deadline).toLocaleString('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' })} UK time`);
      }
    };
    const resize = () => document.documentElement.style.setProperty('--mm-dispatch-height', `${bar.querySelector('.mm-dispatch-bar').getBoundingClientRect().height}px`);
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(bar.querySelector('.mm-dispatch-bar'));
    const refresh = async () => {
      clearTimeout(refreshTimeout);
      const id = ++requestId;
      try {
        const endpoint = new URL(config.root + 'cart', location.origin);
        endpoint.searchParams.set('section_id', 'mm-dispatch-state');
        const previewId = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewId) endpoint.searchParams.set('preview_theme_id', previewId);
        const response = await fetch(endpoint, { cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Dispatch eligibility unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const node = doc.querySelector('[data-mm-dispatch-state]');
        if (!node) throw new Error('Dispatch eligibility unavailable');
        const nextCart = JSON.parse(node.textContent);
        const productNode = doc.querySelector('[data-mm-delivery-recommendations]');
        const nextRecommendations = productNode ? JSON.parse(productNode.textContent) : [];
        if (stopped || id !== requestId) return;
        cart = nextCart;
        recommendedProducts = nextRecommendations;
        config.eligibilityPending = false;
        config.eligibilityUnknown = false;
        render();
      } catch {
        if (stopped || id !== requestId) return;
        cart = { shippingCount: 0, hasGel: false };
        config.eligibilityPending = false;
        config.eligibilityUnknown = true;
        render();
      }
    };
    const scheduleRefresh = () => {
      clearTimeout(refreshTimeout);
      config.eligibilityPending = true;
      render();
      refreshTimeout = setTimeout(refresh, 180);
    };
    const selectOverlayView = (overlay, name, focus = false) => {
      if (!overlay?.querySelector('[data-mm-overlay-panel="'+name+'"]')) return;
      overlay.querySelectorAll('[data-mm-overlay-panel]').forEach(panel => { panel.hidden = panel.dataset.mmOverlayPanel !== name; });
      overlay.querySelectorAll('[data-mm-overlay-tab]').forEach(tab => {
        const selected = tab.dataset.mmOverlayTab === name;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus();
      });
      const body = overlay.querySelector('.mm-overlay-body');
      if (body) body.scrollTop = 0;
    };
    on(document, 'click', event => {
      const tab = event.target.closest('[data-mm-overlay-tab]');
      if (tab) selectOverlayView(tab.closest('dialog'), tab.dataset.mmOverlayTab);
    });
    on(document, 'keydown', event => {
      const tab = event.target.closest('[data-mm-overlay-tab]');
      if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-mm-overlay-tab]')];
      const index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length-1 : (index+(event.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length;
      event.preventDefault();
      selectOverlayView(tab.closest('dialog'), tabs[next].dataset.mmOverlayTab, true);
    });
    on(document, 'mm:overlay:reset', event => selectOverlayView(event.target.closest('dialog'), 'overview'));
    let topupTrigger = trigger;
    const open = source => { topupTrigger = source?.nodeType === 1 ? source : trigger; topupStatus.textContent = ''; if (!dialog.open) { dialog.querySelectorAll('details').forEach(detail => { detail.open = false; }); dialog.dispatchEvent(new CustomEvent('mm:overlay:reset',{bubbles:true})); dialog.showModal(); } trigger.setAttribute('aria-expanded','true'); scheduleRefresh(); };
    const close = () => { dialog.close(); if (topupTrigger.isConnected) topupTrigger.focus(); else trigger.focus(); };
    on(trigger, 'click', open);
    on(document, 'click', event => { const opener = event.target.closest('[data-mm-delivery-open]'); if (opener) { event.preventDefault(); open(opener); } });
    const changeTopup = async (action,product) => {
      if (topupBusy || config.eligibilityPending || config.eligibilityUnknown) return;
      const offer = topupOffer(config,cart);
      if (action === 'add' && offer.mode !== 'offer' || action === 'remove' && !cart.topupQuantity) return;
      topupBusy = true;
      topupStatus.textContent = config.topupText.busy;
      renderTopup();
      try {
        await new Promise((resolve,reject) => {
          if (!document.documentElement.hasAttribute('data-mm-topup-ready')) { reject(new Error(config.topupText.error)); return; }
          document.dispatchEvent(new CustomEvent('mm:cart:topup-request',{detail:{action,parts:offer.parts,product,gap:offer.gap,threshold:offer.threshold,signature:cart.basketSignature,resolve,reject,text:config.topupText}}));
        });
        await refresh();
        if (config.eligibilityUnknown || action === 'add' && cart.topupTotal !== offer.gap || action === 'remove' && cart.topupQuantity > 0) throw new Error(config.topupText.error);
        topupStatus.textContent = action === 'product' ? fill(config.topupText.product_added,{product:product.title}) : action === 'add' ? config.topupText.added : config.topupText.removed;
        if (action === 'product') { topupStatus.tabIndex = -1; topupStatus.focus(); }
      } catch(error) {
        await refresh();
        topupStatus.textContent = error.message || config.topupText.error;
      } finally { topupBusy = false; renderTopup(); }
    };
    on(topupAdd,'click',() => changeTopup('add'));
    on(topupRemove,'click',() => changeTopup('remove'));
    on(suggestionList,'click',event => {
      const button = event.target.closest('[data-mm-topup-product]');
      if (!button) return;
      const product = recommendationsFor(recommendedProducts,cart).find(p => p.variant.id === Number(button.dataset.mmTopupProduct));
      if (product) changeTopup('product',product);
    });
    dialog.querySelectorAll('[data-mm-dispatch-close]').forEach(button => on(button, 'click', close));
    on(dialog,'close',()=>trigger.setAttribute('aria-expanded','false'));
    on(dialog, 'click', event => { if (event.target !== dialog) return; const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close(); });
    on(document, 'mm:cart:updated', scheduleRefresh);
    on(document, 'cart:updated', scheduleRefresh);
    on(window, 'pageshow', event => { if (event.persisted) scheduleRefresh(); });
    on(document, 'visibilitychange', () => { if (!document.hidden) { render(); scheduleRefresh(); } });
    let performanceObserver;
    if ('PerformanceObserver' in window) {
      performanceObserver = new PerformanceObserver(list => {
        if (list.getEntries().some(entry => { const u = new URL(entry.name, location.origin); return u.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(u.pathname); })) scheduleRefresh();
      });
      performanceObserver.observe({ type: 'resource' });
    }
    on(bar, 'pointerenter', () => { hovering = true; });
    on(bar, 'pointerleave', () => { hovering = false; });
    const rotation = setInterval(() => {
      if (!document.hidden && mobileViewport.matches && !reducedMotion.matches && !hovering && !bar.contains(document.activeElement) && !dialog.open) {
        mobileIndex = (mobileIndex + 1) % 2;
        render();
      }
    }, 3000);
    render();
    resize();
    const interval = setInterval(() => { if (!document.hidden) render(); }, 1000);
    cleanup = () => { stopped = true; requestId++; clearInterval(interval); clearInterval(rotation); clearTimeout(refreshTimeout); resizeObserver.disconnect(); performanceObserver?.disconnect(); listeners.forEach(remove => remove()); };
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);

  // DISP-03: store the dispatch rule version, dispatch date and delivery aim shown with the basket,
  // as hidden cart attributes (underscore prefix), so they are saved with the order at checkout.
  (() => {
    let last = '';
    let timer = null;
    let inFlight = false;
    const readRulesVersion = () => {
      try { return JSON.parse(document.querySelector('[data-mm-dispatch-config]')?.textContent || '{}').rulesVersion || ''; } catch { return ''; }
    };
    const ukDate = value => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value));
    const write = async attributes => {
      if (inFlight) return;
      inFlight = true;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
      } catch { last = ''; } finally { inFlight = false; }
    };
    // Insulated packaging is no longer offered (17 Oct 2026): clear any old selection once per page.
    let packagingChecked = false;
    const clearOldPackaging = async () => {
      if (packagingChecked) return;
      packagingChecked = true;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const cart = await (await fetch(root + 'cart.js', { cache: 'no-store' })).json();
        const line = 'Insulated packaging: Yes (complimentary liner + ice pack).';
        const note = (cart.note || '').split('\n').filter(entry => entry.replace(/\r$/, '') !== line).join('\n');
        const legacy = (cart.items || []).filter(item => item.variant_id === 58407598326144);
        for (const item of legacy) await fetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
        if ((cart.attributes && cart.attributes['Insulated packaging']) || note !== (cart.note || '')) {
          await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { 'Insulated packaging': '' }, note }) });
        }
      } catch {}
    };
    document.addEventListener('mm:dispatch:updated', event => {
      clearOldPackaging();
      const detail = event.detail || {};
      const state = detail.state || {};
      const cart = detail.cart || {};
      if (!cart.shippingCount || cart.pending || cart.unknown || state.paused || state.promotion) return;
      const aim = state.deliveryAim;
      const attributes = {
        _dispatch_rules_version: readRulesVersion(),
        _dispatch_date_shown: state.deadline ? ukDate(state.deadline) : '',
        _arrival_aim_shown: aim ? (aim.start === aim.end ? aim.start : aim.start + '/' + aim.end) : ''
      };
      const key = JSON.stringify(attributes) + '|' + (cart.basketSignature || '');
      if (key === last) return;
      last = key;
      clearTimeout(timer);
      timer = setTimeout(() => write(attributes), 1500);
    });
  })();
})();





